import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useNamespace, useVersions } from '../lib/hooks'
import { api } from '../lib/api'

export function ModuleDetail() {
  const { slug = '', name = '', provider = '' } = useParams()
  const ns       = useNamespace(slug)
  const versions = useVersions(slug, name, provider)
  const [busy, setBusy]         = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null)

  const canAdmin = ['owner', 'admin'].includes(ns.data?.role ?? '')

  async function deprecate(version: string) {
    const msg = window.prompt(`Deprecation message for v${version} (optional):`)
    if (msg === null) return
    setBusy(version)
    setFeedback(null)
    try {
      await api.put(`/v1/modules/${slug}/${name}/${provider}/${version}/deprecate`, {
        message: msg || undefined,
      })
      setFeedback({ ok: true, msg: `v${version} deprecated.` })
      versions.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(null)
    }
  }

  const versionList = versions.data?.modules?.[0]?.versions ?? []

  return (
    <Layout
      title={`${slug}/${name}/${provider}`}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          {['owner', 'admin', 'member'].includes(ns.data?.role ?? '') && (
            <Link to={`/namespaces/${slug}/publish`} className="btn btn-primary">
              + Publish version
            </Link>
          )}
          <Link to={`/namespaces/${slug}`} className="btn btn-ghost">
            ← Namespace
          </Link>
        </div>
      }
    >
      {/* Terraform source block */}
      <div className="code-block fu" style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 8 }}>
          // .tf usage
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--text2)' }}>
          <span style={{ color: 'var(--accent)' }}>module</span>{' '}
          <span style={{ color: 'var(--green)' }}>"{name}"</span> {'{'}
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--text2)', paddingLeft: 16 }}>
          source{'  '} = <span style={{ color: 'var(--green)' }}>
            "{window.location.hostname}/{slug}/{name}/{provider}"
          </span>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--text2)', paddingLeft: 16 }}>
          version = <span style={{ color: 'var(--green)' }}>
            "{versionList[0]?.version ?? '0.0.0'}"
          </span>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--text2)' }}>{'}'}</div>
      </div>

      {feedback && (
        <div className={feedback.ok ? 'feedback-success' : 'feedback-error'} style={{ marginBottom: 16 }}>
          {feedback.msg}
        </div>
      )}

      {versions.loading && <div className="empty-state">Loading…</div>}
      {versions.error && (
        <div className="empty-state" style={{ color: 'var(--red)' }}>{versions.error}</div>
      )}

      {versionList.length > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                {canAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {versionList.map((v, i) => (
                <tr key={v.version}>
                  <td>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--green)' }}>
                      v{v.version}
                    </span>
                    {i === 0 && (
                      <span className="badge badge-accent" style={{ marginLeft: 8 }}>latest</span>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-green">published</span>
                  </td>
                  {canAdmin && (
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--amber)' }}
                        disabled={busy === v.version}
                        onClick={() => deprecate(v.version)}
                      >
                        {busy === v.version ? <span className="spinner" /> : 'Deprecate'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!versions.loading && versionList.length === 0 && (
        <div className="empty-state fu">
          <div className="empty-icon">▲</div>
          <div>No versions published yet.</div>
        </div>
      )}

      {/* Dep graph stub */}
      <div className="card fu" style={{ marginTop: 24 }}>
        <div className="card-header">
          <div>
            <div className="kicker">Dependency graph</div>
            <h2 style={{ fontSize: '0.95rem' }}>Module dependencies</h2>
          </div>
        </div>
        <div className="empty-state" style={{ padding: '32px 0' }}>
          <div className="empty-icon">⬡</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text3)' }}>
            Dependency graph visualisation — coming in a future release.
          </div>
        </div>
      </div>
    </Layout>
  )
}
